import React, {
  createContext,
  useCallback,
  Fragment,
  useContext,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

export type Portal<
  T extends Record<string, unknown> = Record<string, unknown>
> = {
  onClose?: () => void;
  id: string;
  element: JSX.Element;
} & T;

const PortalContext = createContext<
  | {
      items: Array<Portal>;
      has: (id: string) => boolean;
      get: (id: string) => Portal<Record<string, unknown>> | undefined;
      close: (id: string) => void;
      open: (element: JSX.Element, options: Omit<Portal, 'element'>) => void;
    }
  | undefined
>(undefined);

export const PortalProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [items, setItems] = useState<Array<Portal>>([]);

  const has = useCallback(
    (id: string) => items.some((e) => e.id === id),
    [items]
  );
  const get = useCallback(
    (id: string) => items.find((e) => e.id === id),
    [items]
  );

  const close = useCallback(
    (portalId: string) => {
      get(portalId)?.onClose?.();
      setItems((prev) => [...prev.filter(({ id }) => id !== portalId)]);
    },
    [get]
  );

  const open = useCallback(
    (element: JSX.Element, options: Omit<Portal, 'element'>) => {
      setItems((prev) => [
        ...prev.filter(({ id }) => id !== options.id),
        { ...options, element } as Portal,
      ]);
    },
    []
  );

  const value = {
    items,
    has,
    get,
    close,
    open,
  };

  return (
    <PortalContext.Provider value={value}>
      {children}
      <Portals />
    </PortalContext.Provider>
  );
};

// eslint-disable-next-line react/prop-types
export const Portals = ({ appendTo = document.body }) => {
  const context = useContext(PortalContext);

  if (!context) {
    throw new Error('usePortals must be used within a PortalProvider');
  }

  const { items } = context;

  return (
    <Fragment>
      {items.map(({ id, element }) => (
        <Fragment key={id}>{ReactDOM.createPortal(element, appendTo)}</Fragment>
      ))}
    </Fragment>
  );
};

export const usePortals = () => {
  const context = useContext(PortalContext);

  if (!context) {
    throw new Error('usePortals must be used within a PortalProvider');
  }

  return context;
};
